import { z } from 'zod';
import { displayNameSchema, usernameSchema } from './auth';

export enum AccountRoles {
  ADMIN = 'ADMIN',
  MODERATOR = 'MODERATOR',
  MENTOR = 'MENTOR',
  ALUMNI = 'ALUMNI',
  PARTNER = 'PARTNER',
}

// This is a list of roles that are allowed to access accelerator routes/endpoints
export const ACCELERATOR_ROLES: Array<AccountRoles> = [
  AccountRoles.ADMIN,
  AccountRoles.MENTOR,
  AccountRoles.ALUMNI,
];

// This is a list of roles that are allowed to access partner routes/endpoints
export const PARTNER_ROLES: Array<AccountRoles> = [
  AccountRoles.ADMIN,
  AccountRoles.PARTNER,
];

export const accessTokenPayloadSchema = z.object({
  _internal_user_id: z.number(),
  _email: z.string().email(),
  _username: z.string().nullable(),
  _display_name: z.string().nullable(),
  _onboarding_complete: z.boolean(),
  _profile_complete: z.boolean(),
  _agreement_complete: z.boolean(),
  _avatar_url: z.string().nullable(),
  _account_roles: z.array(z.string()),
  _accelerator_batches: z.array(z.string()),
});

export type AccessTokenPayload = z.infer<typeof accessTokenPayloadSchema>;

export interface BasicUserProfile {
  id: number;
  username: string;
  displayName: string;
  avatarUrl: string | null;
}

export interface UserProfile extends BasicUserProfile {
  accountRoles: string[];
  batches: string[];
}

export interface UserExtendedProfile {
  country: string;
  city: string;
  languages: string[];
  about: string;
  currentPosition: string;
  isUniversityStudent: boolean;
  githubHandle: string | null;
  twitterHandle: string | null;
  telegramHandle: string | null;
  linkedinHandle: string | null;
  lookingForCollab: boolean;
  lookingToBuild: string;
  jobRoles: string[];
  skills: string[];
  interestedUseCases: string[];
  rolesLookingFor: string[];
}

export type UserAcceleratorProfile = {
  email: string | null;
  telegramHandle: string | null;
  contactOther: string | null;
  profileAppendix: string | null;
};

export const twitterUsernameRegex = /^[A-Za-z0-9_]{4,15}$/;
export const telegramHandleRegex = /^[A-Za-z0-9_]{5,32}$/;
export const githubHandleRegex = /^[A-Za-z0-9-]{1,39}$/;
export const linkedInURLRegex =
  /((https?:\/\/)?((www|\w\w)\.)?linkedin\.com\/)((([\w]{2,3})?)|([^\/]+\/(([\w|\d\-&#?=])+\/?){1,}))$/;

export const profileFields = {
  avatarId: z.number().optional(),
  displayName: displayNameSchema,
  username: usernameSchema,
  city: z
    .string()
    .trim()
    .max(30, { message: 'City cannot be more than 30 characters' }),
  country: z.string().trim(),
  languages: z.string().array().max(8, 'You may select up to 8 languages'),
  about: z
    .string()
    .trim()
    .max(300, 'About you cannot be more than 300 characters'),
  headline: z
    .string()
    .trim()
    .max(30, { message: 'Cannot be more than 30 characters' }),
  telegramHandle: z
    .string()
    .regex(telegramHandleRegex, 'Please enter a valid Telegram username')
    .trim()
    .nullish(),
  twitterHandle: z
    .string()
    .regex(twitterUsernameRegex, 'Please enter a valid X username')
    .trim()
    .nullish(),
  githubHandle: z
    .string()
    .regex(githubHandleRegex, 'Please enter a valid Github username')
    .trim()
    .nullish(),
  linkedinHandle: z
    .string()
    .regex(linkedInURLRegex, 'Please enter a valid full LinkedIn profile URL')
    .trim()
    .nullish(),
  isUniversityStudent: z.boolean().optional(),
} as const;

export const skillsFields = {
  interestedUseCases: z.string().array(),
  rolesLookingFor: z
    .string()
    .array()
    .max(10, { message: 'Please select up to 10 roles' })
    .optional(),
  yourSkills: z
    .string()
    .array()
    .max(20, { message: 'Please select up to 20 skills' })
    .optional(),
  yourRoles: z
    .string()
    .array()
    .max(10, { message: 'Please select up to 10 roles' }),
  lookingToBuild: z.string().trim().optional(),
  lookingForCollab: z.boolean().optional(),
} as const;

export const aboutYouSchema = z.object({
  ...profileFields,
  city: profileFields.city.min(1, { message: 'City is required' }),
  country: profileFields.country.min(1, { message: 'Country is required' }),
  languages: profileFields.languages.min(1, {
    message: 'Please select at least one language',
  }),
  about: profileFields.about.min(1, { message: 'About you is required' }),
  headline: profileFields.headline.min(1, {
    message: 'Headline is required',
  }),
});

export const interestsAndSkillsSchema = z.object({
  ...skillsFields,
  interestedUseCases: skillsFields.interestedUseCases.min(1, {
    message: 'Please select at least one use case.',
  }),
  yourRoles: skillsFields.yourRoles.min(1, {
    message: 'Please select at least one role.',
  }),
  onboardingSource: z
    .string()
    .max(50, { message: 'Cannot be more than 50 characters.' })
    .optional(),
});

// Schema for editing the profile (loose validation, allows for incomplete fields)
export const editProfileSchema = z.object({
  ...profileFields,
  ...skillsFields,
});

// Schema for completing the profile (strict validation, requires all fields to be filled)
export const completeProfileSchema = aboutYouSchema.merge(
  interestsAndSkillsSchema,
);
